import zoomLogoText from '@src/assets/platform-logos/zoom-logotext.svg';
import { useZoomService } from '@src/hooks/useZoomService';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const logger = LogManager.getLogger('component:ZoomAuthButton');

const ZoomAuthButton = ({
  onAuthSuccess,
}: {
  onAuthSuccess?: (tokens: any) => void;
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkTrigger, setCheckTrigger] = useState(0);
  const { userIdentityStore } = useStore();
  const videoCreatorStore = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const zoomService = useZoomService();
  const [searchParams] = useSearchParams();

  // Force a re-check when component mounts or focus returns to window
  useEffect(() => {
    const handleFocus = () => {
      setCheckTrigger(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    // Check URL params for OAuth callback
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get('zoom_auth');

    if (authStatus === 'success') {
      const email = params.get('email');
      const urlPersonId = params.get('personId');

      // CRITICAL: Check if URL personId matches current datoPerson
      if (urlPersonId && urlPersonId !== datoPerson?.person?.id) {
        // Still proceed, but steps.tsx will handle the correct personId
      }

      setIsAuthenticated(true);

      // Force a re-check after successful OAuth
      setCheckTrigger(prev => prev + 1);

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      // Notify parent component with personId
      if (onAuthSuccess) {
        onAuthSuccess({ email, personId: urlPersonId });
      }
    } else if (authStatus === 'error') {
      const message = params.get('message');
      logger.error('Zoom auth error:', message);

      // Handle specific error cases
      if (message === 'missing_oauth_state') {
        videoCreatorStore.showToast({
          state: 'error',
          message:
            'Authentication failed: OAuth state was lost. Please try again.',
        });
      } else if (message === 'missing_user_identity') {
        videoCreatorStore.showToast({
          state: 'error',
          message:
            'Authentication failed: User identity not found. Please refresh and try again.',
        });
      }
    }

    // Check auth status via backend API
    if (authStatus === 'success') {
      // Small delay after OAuth to allow backend to persist tokens
      setTimeout(() => {
        checkExistingAuth();
      }, 1000);
    } else {
      checkExistingAuth();
    }
  }, [datoPerson?.person?.id, onAuthSuccess, checkTrigger]);

  const checkExistingAuth = async (overridePersonId?: string) => {
    // Use override personId (from OAuth URL) or fall back to datoPerson
    const params = new URLSearchParams(window.location.search);
    const urlPersonId = params.get('personId');
    const effectivePersonId =
      overridePersonId || urlPersonId || datoPerson?.person?.id;

    const identity = userIdentityStore.currentIdentity;
    const email = identity?.email || datoPerson?.person?.email;

    if (effectivePersonId || email) {
      const result = await zoomService.checkAuth(effectivePersonId, email);
      if (result.ok && result.value.authenticated) {
        setIsAuthenticated(true);
      }
    }
  };

  const handleConnect = () => {
    setIsLoading(true);

    const identity = userIdentityStore.currentIdentity;
    if (!identity) {
      logger.error('No user identity available for Zoom OAuth');
      videoCreatorStore.showToast({
        state: 'error',
        message: 'Unable to authenticate - no user identity found',
      });
      setIsLoading(false);
      return;
    }

    // Identity.email now always has a value (placeholder for Dato users)
    const userEmail = identity.email || '';

    // Get current URL params to preserve them
    const storyId = searchParams.get('storyId');
    const env = searchParams.get('env');
    const showcase = searchParams.get('showcase');

    // Create state parameter with consistent user identity info
    const stateObject = {
      // Always use the current identity from UserIdentityStore
      userId: identity.id,
      userEmail: userEmail, // Now includes placeholder emails for Dato users
      userSource: identity.source, // 'clerk' or 'dato'
      // Organization info
      organizationId:
        videoCreatorStore.organization?.id ||
        datoPerson.selectedOrganization?.id ||
        datoPerson.getFallbackOrganization()?.id,
      // Person ID if it exists
      personId: datoPerson.person?.id,
      // URL params to restore
      storyId: storyId,
      env: env,
      showcase: showcase,
      // Return to the current page after OAuth
      returnPath: window.location.pathname,
      timestamp: Date.now(),
    };

    // Store state in sessionStorage as backup (in case Zoom loses it)
    const stateBackupKey = `zoom_oauth_state_${Date.now()}`;
    sessionStorage.setItem(stateBackupKey, JSON.stringify(stateObject));

    const state = Buffer.from(
      JSON.stringify({
        ...stateObject,
        stateBackupKey, // Include reference to backup
      }),
    ).toString('base64');

    // Build OAuth URL
    // CRITICAL: redirect_uri must match exactly between authorization request and token exchange
    // Frontend and backend must use the same base URL
    const redirectUri = `${process.env.REACT_APP_API_URL}/api/zoom/oauth-callback`;

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.REACT_APP_ZOOM_CLIENT_ID || '',
      redirect_uri: redirectUri,
      state: state,
    });

    const oauthUrl = `https://zoom.us/oauth/authorize?${params.toString()}`;

    // Redirect to Zoom OAuth
    window.location.href = oauthUrl;
  };

  // Don't render anything if already authenticated
  // The parent component will show the View Recordings button instead
  if (isAuthenticated) {
    return null;
  }

  return (
    <ConnectButton onClick={handleConnect} disabled={isLoading}>
      {isLoading ? (
        'Connecting...'
      ) : (
        <ButtonContent>
          Connect
          <img src={zoomLogoText} alt="Zoom" />
          Account
        </ButtonContent>
      )}
    </ConnectButton>
  );
};

const ConnectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--text-muted);
  border-radius: 6px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--primary-tint-light);
    border-color: var(--primary-hover);
    color: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export default ZoomAuthButton;
